import Phaser from 'phaser';
import {
  runDiagnostics,
  type DiagnosticSnapshot,
} from '../../diagnostic/runDiagnostics.js';
import { Button } from './Button.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Beta diagnostic modal. Opened from SettingsScene → BETA section.
 * Shows a screenshot-friendly snapshot of app + auth + network +
 * data state so testers can attach a photo to bug reports without
 * having to open DevTools.
 *
 * Two states: 'loading' while runDiagnostics() is in flight (~2s
 * for the realtime probe), then 'ready' with the panel filled in.
 * No retry button — the modal is single-shot; close + reopen for a
 * fresh probe.
 *
 * No backdrop-tap cancel — Close button only. Matches the rest of
 * the modal family (ChangeEmail / ResetPassword / DeleteAccount).
 */

const BACKDROP_ALPHA = 0.85;
const BACKDROP_DEPTH = 1000;
const PANEL_DEPTH = 1001;
const PANEL_WIDTH = 620;
const PANEL_HEIGHT = 940;
const PANEL_RADIUS = 12;

// PANEL_LEFT / PANEL_TOP / LABEL_X / VALUE_X are NOT module-level
// constants — see the constructor. They depend on DESIGN_WIDTH /
// DESIGN_HEIGHT, which are imported from PhaserGame.ts. SettingsScene
// imports this modal, PhaserGame imports SettingsScene, this modal
// imports back from PhaserGame → circular dep. At module-init time
// the DESIGN_* bindings resolve to `undefined`, so any module-level
// arithmetic against them yields NaN. Constructor-time computation
// happens AFTER all imports have settled, so the bindings are real
// numbers by then.

const SECTION_GAP = 18;
const ROW_GAP = 8;

type State = 'loading' | 'ready';

export interface BetaDiagnosticModalOptions {
  readonly onClose: () => void;
}

export class BetaDiagnosticModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: BetaDiagnosticModalOptions;
  private readonly chrome: Phaser.GameObjects.GameObject[] = [];
  private bodyObjects: Phaser.GameObjects.GameObject[] = [];
  private buttons: Button[] = [];
  private currentState: State = 'loading';
  private snapshot: DiagnosticSnapshot | null = null;
  private copyStatusText: Phaser.GameObjects.Text | null = null;
  private copyFlashTimer: Phaser.Time.TimerEvent | null = null;
  private destroyed = false;

  // Position fields computed at constructor time to dodge the
  // circular-dep TDZ explained above. Treat as readonly post-init.
  private readonly panelLeft: number;
  private readonly panelTop: number;
  private readonly labelX: number;
  private readonly valueX: number;

  constructor(scene: Phaser.Scene, opts: BetaDiagnosticModalOptions) {
    this.scene = scene;
    this.opts = opts;

    this.panelLeft = DESIGN_WIDTH / 2 - PANEL_WIDTH / 2;
    this.panelTop = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    this.labelX = this.panelLeft + 48;
    this.valueX = this.panelLeft + 250;

    this.drawBackdrop();
    this.drawPanel();
    this.renderBody();

    void this.run();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy();
    });
  }

  private async run(): Promise<void> {
    try {
      const snap = await runDiagnostics();
      if (this.destroyed) return;
      this.snapshot = snap;
      this.currentState = 'ready';
      this.renderBody();
    } catch (err) {
      console.error('runDiagnostics threw', err);
      // Leave the modal in 'loading' — Close still works. Tester
      // reports the bug some other way; this is already the
      // diagnostic for the diagnostic.
    }
  }

  // --- Chrome ---

  private drawBackdrop(): void {
    const bg = this.scene.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        0x000000,
        BACKDROP_ALPHA
      )
      .setDepth(BACKDROP_DEPTH)
      .setInteractive();
    this.chrome.push(bg);
  }

  private drawPanel(): void {
    const panel = this.scene.add.graphics().setDepth(PANEL_DEPTH);
    panel.fillStyle(COLORS.bgDeepInt, 1);
    panel.fillRoundedRect(
      this.panelLeft,
      this.panelTop,
      PANEL_WIDTH,
      PANEL_HEIGHT,
      PANEL_RADIUS
    );
    panel.lineStyle(2, COLORS.accentCyanInt, 0.95);
    panel.strokeRoundedRect(
      this.panelLeft,
      this.panelTop,
      PANEL_WIDTH,
      PANEL_HEIGHT,
      PANEL_RADIUS
    );
    this.chrome.push(panel);
  }

  // --- State-driven body ---

  private renderBody(): void {
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    for (const b of this.buttons) b.destroy();
    this.buttons = [];
    this.copyStatusText = null;

    if (this.currentState === 'loading') {
      this.renderLoading();
    } else {
      this.renderReady();
    }
  }

  private renderLoading(): void {
    this.addCenteredText(
      'BETA DIAGNOSTIC',
      this.panelTop + 48,
      FONT_HEADER,
      24,
      COLORS.accentCyanHex,
      3
    );
    this.addCenteredText(
      'Running checks…',
      DESIGN_HEIGHT / 2,
      FONT_MONO,
      16,
      COLORS.textMuted,
      1
    );
  }

  private renderReady(): void {
    const snap = this.snapshot;
    if (!snap) return;

    this.addCenteredText(
      'BETA DIAGNOSTIC',
      this.panelTop + 36,
      FONT_HEADER,
      24,
      COLORS.accentCyanHex,
      3
    );

    const ts = new Date(snap.timestamp);
    this.addCenteredText(
      formatTimestamp(ts),
      this.panelTop + 78,
      FONT_MONO,
      13,
      COLORS.textMuted,
      1
    );

    let y = this.panelTop + 120;

    y = this.addSection(y, 'APP', [['version', snap.app.version, null]]);

    y = this.addSection(y, 'AUTH', [
      ['user', snap.auth.userIdShort ?? '—', null],
      ['email', snap.auth.emailMasked ?? '—', null],
      [
        'confirmed',
        snap.auth.emailConfirmed ? 'yes' : 'no',
        snap.auth.emailConfirmed
          ? COLORS.accentCyanHex
          : COLORS.accentRedHex,
      ],
      [
        'session',
        snap.auth.sessionValid ? 'valid' : 'expired',
        snap.auth.sessionValid
          ? COLORS.accentCyanHex
          : COLORS.accentRedHex,
      ],
    ]);

    y = this.addSection(y, 'PROFILE', [
      ['username', snap.profile.username ?? '—', null],
      [
        'emails on',
        snap.profile.emailNotifications == null
          ? '—'
          : snap.profile.emailNotifications
            ? 'yes'
            : 'no',
        null,
      ],
    ]);

    const apiOk =
      snap.network.api.status != null &&
      snap.network.api.status >= 200 &&
      snap.network.api.status < 300;
    const apiText =
      snap.network.api.status != null
        ? `${snap.network.api.status} · ${snap.network.api.latencyMs}ms`
        : snap.network.api.error ?? 'unreachable';
    const rtOk = snap.network.realtime.status === 'CONNECTED';
    const rtText = snap.network.realtime.status.toLowerCase().replace(/_/g, ' ');

    y = this.addSection(y, 'NETWORK', [
      ['api', apiText, apiOk ? COLORS.accentCyanHex : COLORS.accentRedHex],
      ['realtime', rtText, rtOk ? COLORS.accentCyanHex : COLORS.accentRedHex],
    ]);

    y = this.addSection(y, 'DATA', [
      [
        'my games',
        snap.data.activeGames == null ? '—' : String(snap.data.activeGames),
        null,
      ],
    ]);

    // Bug-log buffer count. Shows whether any TODO(bug-N)-tagged
    // events have been captured in the run-up to this diagnostic.
    // The events themselves are in the JSON copy output.
    y = this.addSection(y, 'BUG LOG', [
      [
        'events',
        snap.bugLog.length === 0
          ? 'none'
          : `${snap.bugLog.length} buffered`,
        snap.bugLog.length === 0 ? null : COLORS.accentAmberHex,
      ],
    ]);

    // Client section — UA string is long, gets its own wrapped block
    // instead of the kv-row treatment.
    y = this.addSectionHeader(y, 'CLIENT');
    const ua = this.scene.add
      .text(this.labelX, y, snap.client.userAgent, {
        fontFamily: FONT_MONO,
        fontSize: '11px',
        color: COLORS.textMuted,
        wordWrap: { width: PANEL_WIDTH - 96 },
      })
      .setOrigin(0, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(ua);

    // Bottom row: Copy button left, Close button right.
    const buttonY = this.panelTop + PANEL_HEIGHT - 56;
    const cx = DESIGN_WIDTH / 2;

    const copyBtn = new Button(this.scene, {
      x: cx - 130,
      y: buttonY,
      width: 220,
      height: 64,
      text: 'Copy diagnostic',
      style: 'primary',
      onTap: () => this.handleCopy(),
    });
    copyBtn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(copyBtn);

    const closeBtn = new Button(this.scene, {
      x: cx + 130,
      y: buttonY,
      width: 220,
      height: 64,
      text: 'Close',
      onTap: () => this.handleClose(),
    });
    closeBtn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(closeBtn);

    // Copy-success indicator. Invisible until first copy attempt.
    this.copyStatusText = this.scene.add
      .text(cx, buttonY - 44, '', {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.accentCyanHex,
      })
      .setLetterSpacing(1)
      .setOrigin(0.5, 0.5)
      .setDepth(PANEL_DEPTH + 2)
      .setAlpha(0);
    this.bodyObjects.push(this.copyStatusText);
  }

  // --- Section + row helpers ---

  private addSection(
    y: number,
    title: string,
    rows: ReadonlyArray<readonly [string, string, string | null]>
  ): number {
    y = this.addSectionHeader(y, title);
    for (const [label, value, valueColor] of rows) {
      y = this.addKv(y, label, value, valueColor ?? COLORS.textPrimary);
    }
    return y + SECTION_GAP;
  }

  private addSectionHeader(y: number, title: string): number {
    const t = this.scene.add
      .text(this.labelX, y, `> ${title}`, {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
    return y + t.height + 8;
  }

  private addKv(
    y: number,
    label: string,
    value: string,
    valueColor: string
  ): number {
    const labelText = this.scene.add
      .text(this.labelX + 12, y, label, {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textMuted,
      })
      .setOrigin(0, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(labelText);

    const valueText = this.scene.add
      .text(this.valueX, y, value, {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: valueColor,
      })
      .setOrigin(0, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(valueText);

    return y + Math.max(labelText.height, valueText.height) + ROW_GAP;
  }

  private addCenteredText(
    text: string,
    y: number,
    font: string,
    size: number,
    color: string,
    letterSpacing: number
  ): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: font,
        fontSize: `${size}px`,
        color,
        fontStyle: '700',
      })
      .setLetterSpacing(letterSpacing)
      .setOrigin(0.5, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  // --- Actions ---

  private handleCopy(): void {
    if (!this.snapshot) return;
    const json = JSON.stringify(this.snapshot, null, 2);
    void navigator.clipboard
      .writeText(json)
      .then(() => this.flashCopyStatus('Copied to clipboard ✓'))
      .catch((err: unknown) => {
        console.error('clipboard write failed', err);
        this.flashCopyStatus('Copy failed — long-press to select');
      });
  }

  private flashCopyStatus(msg: string): void {
    if (!this.copyStatusText) return;
    this.copyStatusText.setText(msg);
    this.copyStatusText.setAlpha(1);
    this.copyFlashTimer?.remove();
    this.copyFlashTimer = this.scene.time.delayedCall(1500, () => {
      if (this.destroyed || !this.copyStatusText) return;
      this.scene.tweens.add({
        targets: this.copyStatusText,
        alpha: 0,
        duration: 400,
      });
    });
  }

  private handleClose(): void {
    this.opts.onClose();
    this.destroy();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const o of this.chrome) o.destroy();
    this.chrome.length = 0;
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    for (const b of this.buttons) b.destroy();
    this.buttons = [];
    this.copyFlashTimer?.remove();
    this.copyFlashTimer = null;
    this.copyStatusText = null;
  }
}

function formatTimestamp(d: Date): string {
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const yy = d.getFullYear().toString().slice(-2);
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())}.${yy} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
